import {copyLinkToClipboard} from '@/shared/utils/FileHelpers'
import {useContext} from 'react'
import {useNavigate} from 'react-router-dom'
import {Item, Menu, Separator, Submenu} from 'react-contexify'
import {useLayoutPanelsOptional} from '@/shared/context/ChatContext'
import type {CustomFileData} from '@/shared/utils'
import {FileTableContext} from './FileTable'
import useFileTable from './useFileTable'
import {useOntologyTree} from '@/shared/utils/hooks/useOntologyTree'
import {isSingleFileSelected, openInViewerAction} from './file-table-actions'
import {resolveOpenability} from './external-viewer/plain-file-formats'
import {OWN_ROOT, StorageRoot} from '../core/storage'
import {useShareFolderMenu} from '@/app/modules/sharing/ShareFolderMenuItem'
import {useShares, useSharingAccess} from '@/app/modules/sharing/hooks'
import {firstLockedPath} from '@/app/modules/sharing/ownerLocks'
import {
  ShareTransferShare,
  ShareTransferSource,
  useOpenShareTransfer,
} from '@/app/modules/sharing/ShareTransferDialog'
import type {ShareView} from '@/app/modules/sharing/types'
import {usePinActions} from '@/app/modules/pins'
import {folderPinCandidates, insideSharePinCandidates} from './pinnedFolders'

export const MENU_ID = 'fileTableMenu'

const transferShareOf = (share: ShareView): ShareTransferShare => ({
  shareId: share.shareId,
  name: share.name,
  folderPath: share.folderPath,
  ownerName: share.ownerName,
})

const transferSources = (files: CustomFileData[]): ShareTransferSource[] =>
  files.map((file) => ({path: file.id, name: file.name, isDir: file.isDir}))

/**
 * The Files context menu, by what the user may do where they are (D6):
 * - My Files: everything, and "Move or copy to a shared folder…" when someone shares a folder with
 *   Can edit. Renaming or moving a folder the user shares (or one above it) asks first and goes
 *   through the sharing API (E10), so the share follows; moving it into someone else's shared
 *   folder is Copy only.
 * - A folder shared with Can edit: the same file actions, without public links (E9) or sharing, and
 *   "Move or copy to My Files…" (E8).
 * - A folder shared with Can view: Open and Download only.
 *
 * One folder, in My Files or in a shared folder, can be pinned to the top of the folder it is in, or
 * unpinned: a sort order, so even a folder shared with Can view.
 */
export function FileTableMenu({root = OWN_ROOT}: {root?: StorageRoot}) {
  const fileTableContext = useContext(FileTableContext)
  const navigate = useNavigate()
  const layout = useLayoutPanelsOptional()
  const {resolver: ontologyResolver} = useOntologyTree()
  const storageRoot = fileTableContext.storageRoot ?? root
  const {onCopy, onCut, onPaste, filesToCopy, filesToMove, onDownload, onShare, onUnshare} =
    useFileTable(fileTableContext.selectedFiles(), undefined, storageRoot)
  // Nothing to change on the Shared root or in a folder shared with Can view: open and download (D7).
  const readOnly = fileTableContext.readOnly === true
  // Public links are the owner's, for their own files (E9).
  const ownRoot = storageRoot.kind === 'own'
  const {sharing} = useSharingAccess()
  const shares = useShares()
  const openTransfer = useOpenShareTransfer()
  const selected = () => fileTableContext.selectedFiles()

  // "Open" is an OBJECT_OPENING action: available for a single, viewer-openable file. Unknown tags
  // fall through (isOpenable returns undefined) so we do not hide it and regress non-tagged files.
  const canOpenInViewer = () => {
    const files = selected()
    if (!isSingleFileSelected({selectedFiles: files, navigate})) {
      return false
    }
    return resolveOpenability(files[0].id, ontologyResolver.isOpenable(files[0].tags)) !== false
  }
  const isShared = () => {
    return selected().length > 0 && selected().every((file) => file.shared === true && !file.isDir)
  }
  const isDir = () => {
    return selected().length > 0 && selected().every((file) => file.isDir)
  }
  /** E10: why the selection can't be moved into someone else's shared folder; undefined if it can. */
  const nestingReason = () =>
    ownRoot
      ? firstLockedPath(
          selected().map((file) => file.id),
          shares.sharedByMe
        )?.message
      : undefined

  // E8: My Files <-> a folder someone shares with the user, with Can edit.
  const editableShares = shares.sharedWithMe.filter((share) => share.access === 'edit')
  const transferLabel = ownRoot ? 'Move or copy to a shared folder…' : 'Move or copy to My Files…'
  const canTransfer = () =>
    !!openTransfer &&
    selected().length > 0 &&
    (ownRoot
      ? sharing && editableShares.length > 0
      : storageRoot.kind === 'share' && !readOnly && fileTableContext.ownFiles !== false)
  const startTransfer = () => {
    if (!openTransfer || !canTransfer()) return
    const sources = transferSources(selected())
    if (storageRoot.kind === 'share') {
      openTransfer({
        direction: 'toMine',
        share: {
          shareId: storageRoot.id,
          name: storageRoot.name || storageRoot.folderPath.replace(/\/$/, ''),
          folderPath: storageRoot.folderPath,
        },
        sources,
        destination: '',
      })
      return
    }
    openTransfer({
      direction: 'toShare',
      shareChoices: editableShares.map(transferShareOf),
      sources,
      destination: '',
      moveBlockedReason: nestingReason(),
    })
  }

  // Pin to top / Unpin: one folder, while the pins could be read (any signed-in user).
  const pins = usePinActions()
  const pinTarget = () => {
    const files = selected()
    if (!pins.available || files.length !== 1 || !files[0].isDir) {
      return undefined
    }
    const [file] = files
    if (storageRoot.kind === 'share') {
      return insideSharePinCandidates(storageRoot.id, file.id)
    }
    const ownShare = shares.sharedByMe.find((share) => share.folderPath === file.id)
    return folderPinCandidates(file.id, ownShare)
  }
  const pinCandidates = pinTarget()
  const folderPinned = !!pinCandidates && pins.isPinned(...pinCandidates)

  // "Share folder…" (shared folders, P1-F5): renders nothing unless sharing is on for this user.
  const shareFolder = useShareFolderMenu({
    root: storageRoot,
    selectedFiles: fileTableContext.selectedFiles,
    currentFolder: () => fileTableContext.folderPathRef.current,
  })

  const menu = (
    <Menu id={MENU_ID} className='context-menu'>
      <Item
        id={openInViewerAction.id}
        onClick={() =>
          openInViewerAction.run({
            selectedFiles: selected(),
            navigate,
            expandCenter: layout?.expandCenter,
            root: fileTableContext.storageRoot,
          })
        }
        hidden={() => !canOpenInViewer()}
      >
        {openInViewerAction.label}
      </Item>
      {canOpenInViewer() && !readOnly && <Separator />}
      {!readOnly && (
        <Submenu label='New' arrow='>'>
          <Item onClick={() => fileTableContext.onNewFolder()}>Folder</Item>
        </Submenu>
      )}
      {!readOnly && <Separator />}
      <Item onClick={onCopy} id='context-copy' hidden={readOnly}>
        {'Copy'}
        <div className='contexify_rightSlot'>⌘/Ctrl C</div>
      </Item>
      <Item onClick={onCut} id='context-cut' hidden={readOnly}>
        {'Cut'}
        <div className='contexify_rightSlot'>⌘/Ctrl X</div>
      </Item>
      <Item
        onClick={() => onPaste(fileTableContext.folderPathRef.current)}
        hidden={() =>
          readOnly || (filesToCopy.current.length === 0 && filesToMove.current.length === 0)
        }
        id='context-paste'
      >
        {'Paste'}
        <div className='contexify_rightSlot'>⌘/Ctrl V</div>
      </Item>
      <Item
        onClick={() => {
          fileTableContext.setShowRenameModal(true)
          fileTableContext.onRename()
        }}
        hidden={() => readOnly || selected().length !== 1}
        id='context-rename'
      >
        Rename
      </Item>
      <Item onClick={startTransfer} hidden={() => !canTransfer()} id='context-transfer'>
        {transferLabel}
      </Item>
      <Item
        id='context-pin'
        hidden={() => !pinTarget()}
        onClick={() => {
          const candidates = pinTarget()
          if (!candidates) return
          if (pins.isPinned(...candidates)) {
            pins.unpin(...candidates)
          } else {
            pins.pin(candidates[0])
          }
        }}
      >
        {folderPinned ? 'Unpin' : 'Pin to top'}
      </Item>
      {!readOnly && <Separator />}
      <Item onClick={onDownload} id='context-download'>
        Download
      </Item>
      <Item onClick={onShare} hidden={() => !ownRoot || isShared() || isDir()} id='context-share'>
        Share
      </Item>
      <Item
        onClick={() => copyLinkToClipboard(selected())}
        hidden={() => !ownRoot || !isShared()}
        id='context-link'
      >
        Copy link
      </Item>
      <Item
        id='context-unshare'
        onClick={() => {
          onUnshare()
          fileTableContext.resetSelection()
        }}
        hidden={() => !ownRoot || !isShared()}
      >
        Unshare
      </Item>
      {shareFolder.items}
      {!readOnly && <Separator />}
      <Item
        id='context-delete'
        hidden={readOnly}
        onClick={() => {
          fileTableContext.setShowDeleteConfirmation(true)
        }}
      >
        Delete
      </Item>
    </Menu>
  )

  return (
    <>
      {menu}
      {shareFolder.dialog}
    </>
  )
}
